<template>
  <div class="flex flex-col w-full" style="position: relative; width: 100%">
    <div class="flex !grow-0 items-center gap-4 pa-2" style="width: 100%; font-weight: bold; overflow: auto">
      <div class="cursor-pointer flex items-center gap-2" @click="showConfigurationTab()">
        <v-icon>mdi-cog</v-icon>
        <span :class="{ selectedTab: isConfigurationTabVisible }">{{ $t('Simulation.Configuration') }}</span>
      </div>
      |
      <div class="cursor-pointer flex items-center gap-2" @click="showFormationTab()">
        <v-icon>mdi-vector-circle</v-icon>
        <span :class="{ selectedTab: isFormationTabVisible }">{{ $t('Simulation.Formation') }}</span>
      </div>
      |
      <div class="cursor-pointer flex items-center gap-2" @click="showMissionPlanTab()">
        <v-icon>mdi-group</v-icon>
        <span :class="{ selectedTab: isMissionPlanTabVisible }">{{ $t('Simulation.MainMissionPlan') }}</span>
      </div>
      <v-spacer />
      <div class="flex items-center gap-1">
        <v-btn class="ma-0" icon small @click="bestFit()"><v-icon>mdi-target</v-icon></v-btn>
      </div>
    </div>
    <div class="grow w-full" style="position: relative; background-color: white">
      <div class="scrollable-container" :class="{ 'mx-4': !isMissionPlanTabVisible, 'mx-2': isMissionPlanTabVisible }">
        <SimulationDetailParameter covMode v-if="isConfigurationTabVisible" />
        <FormationParameter v-if="isFormationTabVisible" ref="formationParam" />
        <FormationDetailMainMissionPlan v-if="isMissionPlanTabVisible" />
      </div>
    </div>
    <div class="flex shrink-0 !grow-0 w-full mt-auto">
      <v-btn :disabled="!isRunnable" class="!grow-0" @click="runFormationSimulation()" small color="primary">
        {{ $t('Simulation.Run') }}
        <v-progress-circular
          class="ml-2"
          :size="20"
          v-if="isCoverageRunning"
          indeterminate
          color="white"
        ></v-progress-circular>
        <v-icon v-else>mdi-play</v-icon>
      </v-btn>
      <v-btn :disabled="!btnActif" @click="saveConfig()" small color="primary" class="ml-auto !grow-0">
        {{ $t('Pilot.MissionPlan.PublishStagingToIris') }}
        <v-icon small right>mdi-content-save</v-icon>
      </v-btn>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, nextTick, getCurrentInstance } from 'vue'
import { MissionPlanUiState, buildMissionPlanStateId } from '@/models/ui/MissionPlanUiState'
import SimulationDetailParameter from '@/components/smart/SimulationDetailParameter.vue'
import deepEqual from 'deep-equal'
import { i18n } from '@/locale/vueI18n'
import { buildSimulationUIStateId } from '@/models/ui/SimulationUiState'
import { buildCoverageStateId } from '@/models/ui/CoverageUiState'
import { AloDbMissionGlobalFormation } from '@/models/alodb/AloDbMissionGlobal'
import FormationParameter from '@/components/smart/Formation/FormationParameter.vue'
import FormationDetailMainMissionPlan from '@/components/smart/Formation/FormationDetailMainMissionPlan.vue'
import store from '@/store'
import { isReadyForSimulationFormation } from '@/models/iris/smartMission/ISmartSimulationFormation'
import { VuetifyConfirmObject } from 'vuetify-confirm/types'

const props = defineProps<{
  // Whether in coverage mode (true) or formation mode (false)
  isVisible: boolean
}>()

const instance = getCurrentInstance()
const $confirm = instance?.proxy?.$confirm!

const visibleTab = ref('configuration') // configuration || formation || missionplan
const formationParam = ref()

const isMissionPlanTabVisible = computed(() => visibleTab.value === 'missionplan')
const isFormationTabVisible = computed(() => visibleTab.value === 'formation')
const isConfigurationTabVisible = computed(() => visibleTab.value === 'configuration')

const modified = computed(() => store.myGetters.modifiedAdmMissionGlobal as AloDbMissionGlobalFormation)
const original = computed(() => store.myGetters.originalAdmMissionGlobal as AloDbMissionGlobalFormation)

const editCoverageStateUiid = computed(() => buildCoverageStateId(modified.value.MIG_ID || -1, 'detail'))
const editCoverageState = computed(() =>
  editCoverageStateUiid.value ? store.myGetters.coverageStateByUiid(editCoverageStateUiid.value) : null,
)

const map_uiid = computed(() => buildSimulationUIStateId(original.value.MIG_ID!, 'map'))

const isCoverageRunning = computed(() => editCoverageState.value?.runningCoverage)
const isRunnable = computed(() => !isCoverageRunning.value)

const btnActif = computed(() => !deepEqual(original.value, modified.value) && !editCoverageState.value!.saving)

const missionPlanUiid = computed(() => buildMissionPlanStateId(modified.value.MIG_ID || -1, 'formation'))
const missionPlanUiState = computed(() => store.myGetters.missionPlanUiStateByUiid(missionPlanUiid.value))

const plan = computed({
  get: () => missionPlanUiState.value.missionPlan,
  set: value => {
    store.myActions.UI_MISSIONPLAN_SET_MISSIONPLAN({
      uiid: missionPlanUiid.value,
      plan: value,
    })
  },
})

const mission_plan = computed(() => modified.value.config.mission_plan)
const isOperator = computed(() => store.myGetters.isOperator)
const isSysAdmin = computed(() => store.myGetters.isSysAdmin)

function showMissionPlanTab() {
  visibleTab.value = 'missionplan'
}

function showFormationTab() {
  visibleTab.value = 'formation'
}

function showConfigurationTab() {
  visibleTab.value = 'configuration'
}

function bestFit() {
  store.myActions.UI_REFRESH_BEST_FIT_MAP(map_uiid.value)
}

async function saveConfig(executeNext = async () => {}) {
  editCoverageState.value!.drawMode = ''
  editCoverageState.value!.currentEditWpt = MissionPlanUiState.getDefaultWayPoint()
  editCoverageState.value!.hoverWptKey = ''
  store.myActions.UI_SET_MEASURETOOL('NONE')

  await nextTick(async () => {
    editCoverageState.value!.saving = true
    await store.myActions.MISSIONGLOBAL_SAVE_REQUEST()
    editCoverageState.value!.selectedCoverageId = null
    await executeNext()
    editCoverageState.value!.saving = false
  })
}

function runFormationSimulation() {
  editCoverageState.value!.runningCoverage = true
  saveConfig(doRunFormationSimulation)
}

async function doRunFormationSimulation() {
  const errors = isReadyForSimulationFormation(original.value.config!)
  //console.log(errors)
  if (!errors?.length) {
    editCoverageState.value!.runningCoverage = true
    try {
      await store.myActions.MISSIONGLOBAL_RUN_FORMATION_SIMULATION_REQUEST()
    } finally {
      editCoverageState.value!.runningCoverage = false
    }
  } else {
    editCoverageState.value!.runningCoverage = false
    $confirm(`<ul><li>${errors.map(e => i18n.t(`Simulation.Check.${e}`)).join('</li><li>')}</li></ul>`, {
      title: i18n.t('Simulation.Check.Title'),
      buttonTrueText: 'Ok',
      buttonFalseText: '',
    } as VuetifyConfirmObject)
  }
}

// Watch for changes in original to update plan
watch(original, () => {
  if (!btnActif.value) {
    plan.value = mission_plan.value
  }
})
</script>
